import React, { useState } from 'react';
import { toast } from 'react-toastify';

import { QueryForm } from '../../types/Query';
import { createQuery } from '../../api/Query';
import '../../css/modal.css';

interface CreateQueryModalProps {
  customerId: number;
  onClose: () => void;
  onQueryCreated: (queryId: number) => void;
}

const CreateQueryModal: React.FC<CreateQueryModalProps> = ({ customerId, onClose, onQueryCreated }) => {
  const [formData, setFormData] = useState<QueryForm>({
    customerId: customerId,
    type: '',
    source: '',
    location: '',
    description: '',
    status: '',
    queryDate: '',
    pax: 2,
    engagementLevel: '1 - Initial Inquiry',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    if (!formData.type) {
      toast.warning('Please select a query type.');
      return false;
    }
    if (!formData.source) {
      toast.warning('Please select a query source.');
      return false;
    }
    if (!formData.location.trim()) {
      toast.warning('Please enter a location.');
      return false;
    }
    if (!formData.pax || formData.pax <= 0) {
      toast.warning('Please enter a valid PAX number.');
      return false;
    }
    if (!formData.queryDate) {
      toast.warning('Please select a travel date.');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const query = {
      ...formData,
      customerId,
    };

    try {
      const newQuery = await createQuery(query);
      toast.success('Query created successfully!');
      onQueryCreated(newQuery.id);
    } catch (error) {
      toast.error('Failed to create query.');
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal">
        <h2>Add Query</h2>

        <label>
          Type<span className="required">*</span>
          <select name="type" value={formData.type} onChange={handleChange} required>
            <option value="">Select type</option>
            <option value="domestic">Domestic</option>
            <option value="international">International</option>
            <option value="flight">Flight</option>
            <option value="hotel">Hotel</option>
            <option value="event">Event</option>
            <option value="visa">VISA</option>
          </select>
        </label>

        <label>
          Source<span className="required">*</span>
          <select name="source" value={formData.source} onChange={handleChange} required>
            <option value="">Select query source</option>
            <option value="walk-in">Walk In</option>
            <option value="referral">Referral</option>
            <option value="market-visit">Market Visit</option>
            <option value="online">Online</option>
            <option value="others">Others</option>
          </select>
        </label>

        <label>
          Location<span className="required">*</span>
          <input name="location" placeholder="e.g. Goa, Paris" value={formData.location} onChange={handleChange} />
        </label>

        <label>
          PAX<span className="required">*</span>
          <input name="pax" type="number" placeholder="Number of people" value={formData.pax} onChange={handleChange} />
        </label>

        <label>
          Status
          <select name="status" value={formData.status} onChange={handleChange}>
            <option value="">Select status</option>
            <option value="open">Open</option>
            <option value="in-progress">In Progress</option>
            <option value="closed">Closed</option>
          </select>
        </label>

        <label>
          Engagement Level
          <select name="engagementLevel" value={formData.engagementLevel} onChange={handleChange}>
            <option value="1 - Initial Inquiry">1 - Initial Inquiry</option>
            <option value="2 - Basic Details Shared">2 - Basic Details Shared</option>
            <option value="3 - Discussed Options">3 - Discussed Options</option>
            <option value="4 - Proposal Shared">4 - Proposal Shared</option>
            <option value="5 - Hot Lead">5 - Hot Lead</option>
          </select>
        </label>

        <label>
          Travel Date<span className="required">*</span>
          <input type="date" name="queryDate" value={formData.queryDate} onChange={handleChange} />
        </label>

        <label>
          Description
          <textarea
            name="description"
            placeholder="Additional details about the query..."
            value={formData.description}
            onChange={handleChange}
          />
        </label>

        <div className="modal-actions">
          <button onClick={handleSubmit}>Save</button>
          <button onClick={onClose} className="secondary">Cancel</button>
        </div>
      </div>
    </div>
  );
};

export default CreateQueryModal;
